import { useForm } from '@inertiajs/react'
import { useEffect } from 'react'
import { Badge, Button, Flex, Select, Separator, Text, TextArea } from '@radix-ui/themes'
import { badgeColors } from '@/shared/constants/constants'
import { TrackManagerCredentialsIndexCredential } from '@/types/track_manager_credentials_index'

interface Props {
  credential: TrackManagerCredentialsIndexCredential
  /** Rendered in a mobile card that already shows the email, form, track and status. */
  compact?: boolean
}

export default function CredentialReviewForm({ credential, compact = false }: Props) {
  const { data, setData, patch, processing } = useForm({
    status: credential.status,
    reviewer_notes: credential.reviewer_notes ?? '',
    rejection_reason: credential.rejection_reason ?? '',
  })

  // Re-initialize when the drawer moves to a different credential.
  useEffect(() => {
    setData({
      status: credential.status,
      reviewer_notes: credential.reviewer_notes ?? '',
      rejection_reason: credential.rejection_reason ?? '',
    })
  }, [credential.id])

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    patch(`/track_manager/credentials/${credential.id}`, { preserveState: true, preserveScroll: true })
  }

  return (
    <form onSubmit={handleSubmit}>
      {/* The card already carries the email, form, track and status — but not the
          category or the expiry date, so those two survive `compact`. */}
      <Flex direction="column" gap="1" mb="3">
        {!compact && (
          <>
            <Text size="3" weight="bold">
              {credential.email}
            </Text>
            <Text size="1" color="gray">
              {credential.form_title} — {credential.track_name}
            </Text>
          </>
        )}
        <Flex gap="2" mt="1" align="center">
          <Badge variant="soft" color="gray" style={{ textTransform: 'capitalize' }}>
            {credential.category}
          </Badge>
          {!compact && (
            <Badge variant="soft" color={badgeColors[credential.status]} style={{ textTransform: 'capitalize' }}>
              {credential.status}
            </Badge>
          )}
          {credential.expired ? (
            !compact && (
              <Badge variant="soft" color="crimson">
                Expired
              </Badge>
            )
          ) : (
            credential.expires_at && (
              <Text size="1" color="gray">
                Expires {credential.expires_at}
              </Text>
            )
          )}
        </Flex>
      </Flex>

      <Separator size="4" mb="3" />

      <Flex direction="column" gap="2" mb="3">
        {credential.fields.map((field) => (
          <Flex key={field.key} direction="column" gap="0">
            <Text size="1" color="gray">
              {field.label}
            </Text>
            <Text size="2">{field.value}</Text>
          </Flex>
        ))}
        {credential.fields.length === 0 && (
          <Text size="2" color="gray">
            No answers submitted.
          </Text>
        )}
      </Flex>

      <Separator size="4" mb="3" />

      <Flex direction="column" gap="1" mb="3">
        <Text size="2" as="label" weight="medium">
          Rejection Reason
        </Text>
        <TextArea
          value={data.rejection_reason}
          onChange={(e) => setData('rejection_reason', e.target.value)}
          placeholder="Emailed to the applicant — tell them what to fix"
          rows={3}
        />
      </Flex>

      <Flex direction="column" gap="1" mb="3">
        <Text size="2" as="label" weight="medium">
          Reviewer Notes
        </Text>
        <TextArea
          value={data.reviewer_notes}
          onChange={(e) => setData('reviewer_notes', e.target.value)}
          placeholder="Internal — the applicant never sees this"
          rows={3}
        />
      </Flex>

      <Flex direction="column" gap="1" mb="4">
        <Text size="2" as="label" weight="medium">
          Status
        </Text>
        <Select.Root value={data.status} onValueChange={(v) => setData('status', v as typeof data.status)}>
          <Select.Trigger />
          <Select.Content>
            <Select.Item value="pending">Pending</Select.Item>
            <Select.Item value="approved">Approved</Select.Item>
            <Select.Item value="rejected">Rejected</Select.Item>
          </Select.Content>
        </Select.Root>
      </Flex>

      <Button type="submit" loading={processing} style={{ width: '100%' }}>
        Save
      </Button>
    </form>
  )
}
